import { CheckboxField } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons'
import { Loader } from '@calendly/ui/components/loader'
import { BodyText } from '@calendly/ui/components/typography'
import { colorIconInfo } from '@calendly/ui/theme'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { selectById as selectCalendarConnectionById, useFetchCalendarAccountsQuery } from 'features/calendar_connections'

import { useActiveUnavailableStatusesString } from './active_unavailable_statuses'
import {
  AccordionBody,
  AccordionContainer,
  AccordionHeader,
  AccordionHeaderButton,
  AccordionHeaderContentWrapper,
  Option,
} from './styles'


const ExpandableIcon = ({ isExpanded }) => {
  if (isExpanded) return <ArrowChevronUpMiniIcon color={colorIconInfo} />
  return <ArrowChevronDownMiniIcon color={colorIconInfo} />
}

const useCurrentCalendarConnection = () => {
  const { id } = useParams()
  const { data, ...rest } = useFetchCalendarAccountsQuery()
  const calendarConnectionData = selectCalendarConnectionById(data, id)

  return { ...rest, data: calendarConnectionData }
}

const UnavailableStatusesAccordion = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const unavailableStatusesId = 'unavailable-statuses-selection'
  const { data: { localizedUnavailableStatuses } } = useCurrentCalendarConnection()
  const statuses = useActiveUnavailableStatusesString(localizedUnavailableStatuses)

  return (
    <AccordionContainer>
      <AccordionHeader role="heading" aria-level={2}>
        <AccordionHeaderButton
          onClick={() => setIsExpanded((currentState) => !currentState)}
          aria-label={I18n.t('publisher.availability_settings.section.calendar_settings.check_conflicts.popup.open_unavailable')}
          aria-expanded={isExpanded}
          aria-controls={isExpanded ? unavailableStatusesId : undefined}
        >
          <AccordionHeaderContentWrapper>
            <BodyText weight="bold">
              {I18n.t('publisher.availability_settings.section.calendar_settings.check_conflicts.popup.unavailable')}
            </BodyText>
            <BodyText>
              {isExpanded ? I18n.t(
                'publisher.availability_settings.section.calendar_settings.check_conflicts.popup.unavailable_description_expanded'
              ) : I18n.t(
                'publisher.availability_settings.section.calendar_settings.check_conflicts.popup.unavailable_description', { statuses }
              )}
            </BodyText>
          </AccordionHeaderContentWrapper>
          <ExpandableIcon isExpanded={isExpanded} />
        </AccordionHeaderButton>
      </AccordionHeader>
      {isExpanded && (
        <AccordionBody id={unavailableStatusesId} role="region">
          {children}
        </AccordionBody>
      )}
    </AccordionContainer>
  )
}

export const UnavailableStatuses = () => {
  const { data: { canSelectUnavailableStatus, localizedUnavailableStatuses }, isLoading } = useCurrentCalendarConnection()

  if (isLoading) return <Loader />
  if (!canSelectUnavailableStatus) return null

  return (
    <UnavailableStatusesAccordion>
      {
        localizedUnavailableStatuses.map(({ id: calendarId, name: calendarLabel, forced }) => (
          <Option key={calendarId}>
            <CheckboxField
              name="unavailableStatuses"
              label={calendarLabel}
              value={calendarId}
              disabled={forced}
            />
          </Option>
        ))
      }
    </UnavailableStatusesAccordion>
  )
}
